import React, { useState, useEffect } from "react";
import axios from "axios";
import "./Task.css";
import { FaPlus, FaTimes } from "react-icons/fa";
import AlertModal from "../AlertModal/AlertModal";
import config from "../../config";
import useAuth from "../../Auth/useAuth";

const getUsersUrl = `${config.BASE_URL}/list-users`;
const createTaskUrl = `${config.BASE_URL}/create-task`;
const getTaskDataUrl = `${config.BASE_URL}/list-all-tasks`;
const updateTaskDataUrl = `${config.BASE_URL}/update-task`;
const deleteTaskDataUrl = `${config.BASE_URL}/delete-task`;

const Task = () => {
  const { username, userProfile, setActiveModule } = useAuth();

  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    taskName: "",
    taskDescription: "",
    taskPriority: "",
    taskAssignedTo: "",
  });

  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    record: null,
  });

  // Add modal state
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
  });

  // Modal handler functions
  const showModal = (title, message, type = "info") => {
    setModalConfig({
      isOpen: true,
      title,
      message,
      type,
    });
  };

  const closeModal = () => {
    setModalConfig((prev) => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    sessionStorage.setItem("actMod", "task");
    setActiveModule(sessionStorage.getItem("actMod"));
    fetchAllUsers();
    fetchAllTasks();
  }, [setActiveModule]);

  const fetchAllUsers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        getUsersUrl,
        { userProfile: userProfile },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        },
      );
      console.log("Users:", response.data);
      setUsers(response.data || []);
    } catch (err) {
      console.error("Error fetching users list:", err);
      setError("Failed to load users. Please try again.");
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllTasks = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        getTaskDataUrl,
        { username: username, userProfile: userProfile },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        },
      );
      console.log("Tasks:", response.data);
      setTasks(response.data || []);
    } catch (error) {
      console.error("Error fetching tasks list", error);
      setError("Failed to load tasks. Please try again.");
      setTasks([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssignment = () => {
    setIsModalOpen(true);
    setFormData({
      taskName: "",
      taskDescription: "",
      taskPriority: "",
      taskAssignedTo: "",
    });
    setError(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setError(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitTask = async () => {
    setIsSubmitting(true);
    setError(null);
    if (
      !formData.taskName ||
      !formData.taskDescription ||
      !formData.taskPriority ||
      !formData.taskAssignedTo
    ) {
      setError("Please fill in all task details.");
      setIsSubmitting(false);
      return;
    }

    try {
      const taskData = {
        ...formData,
        createdBy: username,
        status: "Pending",
      };
      console.log("Submitting task data:", taskData);
      const response = await axios.post(createTaskUrl, taskData, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });

      if (response.data) {
        showModal("Success", "Task created successfully!", "success");
        handleCloseModal();
        fetchAllTasks();
      }
    } catch (err) {
      console.error("Error creating task:", err);
      setError("Failed to create task. Please try again.");
      showModal("Error", "Failed to create task. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseEditMode = () => {
    setEditingId(null);
    setEditingData({});
  };

  const handleEditingChange = (e) => {
    const { name, value } = e.target;
    setEditingData({ ...editingData, [name]: value });
  };

  const handleEditTask = (taskId) => {
    const taskToEdit = tasks.find((task) => task._id === taskId);
    if (taskToEdit) {
      setEditingId(taskId);
      setEditingData({
        taskName: taskToEdit.taskName,
        taskDescription: taskToEdit.taskDescription,
        taskPriority: taskToEdit.taskPriority,
        taskAssignedTo: taskToEdit.taskAssignedTo,
        status: taskToEdit.status,
      });
    }
  };

  const handleSaveTask = async (taskId) => {
    setIsSubmitting(true);
    //Add taskId : _id here
    editingData.id = taskId;
    console.log("Editing data:", editingData);
    try {
      const response = await axios.put(`${updateTaskDataUrl}`, editingData, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });

      if (response.data) {
        setTasks(
          tasks.map((task) =>
            task._id === taskId ? { ...task, ...editingData } : task,
          ),
        );
        setEditingId(null);
        fetchAllTasks();
        showModal("Success", "Task updated successfully!", "success");
      }
    } catch (error) {
      console.error("Error updating task:", error);
      showModal("Error", "Failed to update task. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTask = (task) => {
    console.log("Delete button clicked for ID:", task);
    setDeleteModal({
      isOpen: true,
      record: task,
    });
  };

  const confirmDelete = async () => {
    const recordId = deleteModal.record?._id;
    if (!recordId) return;

    setIsSubmitting(true);
    try {
      const response = await axios.delete(`${deleteTaskDataUrl}/${recordId}`, {
        withCredentials: true,
      });
      console.log("Delete response:", response.data);
      fetchAllTasks();
      setTasks(tasks.filter((task) => task._id !== recordId));
      setDeleteModal({ isOpen: false, recordId: null });
      showModal("Success", "Task deleted successfully!", "success");
    } catch (error) {
      console.error("Error deleting task:", error);
      showModal("Error", "Failed to delete task. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div className="task-container">
      <AlertModal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
      />
      {/* You could add a loading indicator here for the initial user fetch too */}
      {isLoading && (
        <div className="spinner-container">
          <div className="spinner"></div>
          <span>Loading Task...</span>
        </div>
      )}
      {!isLoading && userProfile === "admin" && (
        <div className="task-assignment">
          <button className="assign-button" onClick={handleAssignment}>
            <FaPlus /> New Task
          </button>
        </div>
      )}
      {error && !isModalOpen && (
        <div style={{ color: "red", textAlign: "center", marginTop: "1rem" }}>
          {error}
        </div>
      )}
      {isModalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div
            className={`modal-content ${isSubmitting ? "loading" : ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Create Task</h3>
              <button
                className="close-button"
                onClick={handleCloseModal}
                disabled={isSubmitting}
              >
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              {error && isModalOpen && (
                <div
                  style={{
                    color: "red",
                    textAlign: "center",
                    marginBottom: "1rem",
                  }}
                >
                  {error}
                </div>
              )}
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="taskAssignedTo">Assign To</label>
                  <select
                    id="taskAssignedTo"
                    name="taskAssignedTo"
                    value={formData.taskAssignedTo}
                    onChange={handleInputChange}
                    className="form-control"
                    disabled={isSubmitting}
                  >
                    <option value="">Select User</option>
                    {isLoading ? (
                      <option disabled>Loading users...</option>
                    ) : users.length > 0 ? (
                      users.map((user) => (
                        <option key={user._id} value={user.username}>
                          {user.username} ({user.profile})
                        </option>
                      ))
                    ) : (
                      <option disabled>No users available</option>
                    )}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="taskPriority">Priority</label>
                  <select
                    id="taskPriority"
                    name="taskPriority"
                    value={formData.taskPriority}
                    onChange={handleInputChange}
                    className="form-control"
                    disabled={isSubmitting}
                  >
                    <option value="">Select Priority</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="taskName">Task Name</label>
                  <input
                    type="text"
                    id="taskName"
                    name="taskName"
                    autoComplete="off"
                    value={formData.taskName}
                    onChange={handleInputChange}
                    className="form-control"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Task Description - Using textarea for multi-line input */}
                <div className="form-group full-width">
                  <label htmlFor="taskDescription">Description</label>
                  <textarea
                    id="taskDescription"
                    name="taskDescription"
                    autoComplete="off"
                    value={formData.taskDescription}
                    onChange={handleInputChange}
                    className="form-control"
                    rows="4"
                    maxLength={100}
                    disabled={isSubmitting}
                  ></textarea>
                  <div
                    style={{
                      fontSize: "0.8rem",
                      textAlign: "right",
                      marginTop: "4px",
                    }}
                  >
                    {100 - formData.taskDescription.length} characters remaining
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={handleCloseModal}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                className="btn-create"
                onClick={handleSubmitTask}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Creating..." : "Create Task"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="task-table-wrapper">
        {isLoading && (
          <div className="modal-spinner-overlay">
            <div className="spinner"></div>
            <span>Loading task data...</span>
          </div>
        )}

        {!isLoading && tasks.length > 0 ? (
          <div className="task-scrollable-table">
            <table className="task-table">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Description</th>
                  <th>Priority</th>
                  {userProfile !== "admin" && <th>Created By</th>}
                  {userProfile === "admin" && <th>Assigned To</th>}
                  <th>Status</th>
                  <th>Edit</th>
                  <th>Delete</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => (
                  <tr key={task._id}>
                    <td>
                      {userProfile === "admin" && editingId === task._id ? (
                        <input
                          name="taskName"
                          value={editingData.taskName}
                          onChange={handleEditingChange}
                          className="edit-input"
                        />
                      ) : (
                        task.taskName
                      )}
                    </td>
                    <td>
                      {userProfile === "admin" && editingId === task._id ? (
                        <div
                          style={{ display: "flex", flexDirection: "column" }}
                        >
                          <textarea
                            name="taskDescription"
                            value={editingData.taskDescription}
                            onChange={handleEditingChange}
                            className="edit-input"
                            rows="4"
                            maxLength={100}
                          />
                          <div
                            style={{
                              fontSize: "0.8rem",
                              textAlign: "right",
                              marginTop: "4px",
                              color: "#666",
                            }}
                          >
                            {100 - editingData.taskDescription.length}{" "}
                            characters remaining
                          </div>
                        </div>
                      ) : (
                        task.taskDescription
                      )}
                    </td>
                    <td>
                      {userProfile === "admin" && editingId === task._id ? (
                        <select
                          name="taskPriority"
                          value={editingData.taskPriority}
                          onChange={handleEditingChange}
                          className="edit-input"
                        >
                          <option value="High">High</option>
                          <option value="Medium">Medium</option>
                          <option value="Low">Low</option>
                        </select>
                      ) : (
                        task.taskPriority
                      )}
                    </td>
                    {userProfile !== "admin" && <td>{task.createdBy}</td>}
                    {userProfile === "admin" && (
                      <td>
                        {editingId === task._id ? (
                          <select
                            name="taskAssignedTo"
                            value={editingData.taskAssignedTo}
                            onChange={handleEditingChange}
                            className="edit-input"
                          >
                            {users.map((user) => (
                              <option key={user._id} value={user.username}>
                                {user.username}
                              </option>
                            ))}
                          </select>
                        ) : (
                          task.taskAssignedTo
                        )}
                      </td>
                    )}
                    <td>
                      {userProfile !== "admin" && editingId === task._id ? (
                        <select
                          name="status"
                          value={editingData.status}
                          onChange={handleEditingChange}
                          className="edit-input"
                        >
                          <option value="">Select</option>
                          {/* <option value="Pending">Pending</option> */}
                          <option value="In Progress">In Progress</option>
                          <option value="Completed">Completed</option>
                          <option value="Cancelled">Cancelled</option>
                        </select>
                      ) : (
                        task.status
                      )}
                    </td>
                    <td>
                      {editingId === task._id ? (
                        <div className="edit-btn-container">
                          <button
                            className="edit-btn-cancel"
                            onClick={handleCloseEditMode}
                          >
                            Cancel
                          </button>
                          <button
                            className="save-btn"
                            onClick={() => handleSaveTask(task._id)}
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? "Saving..." : "Save"}
                          </button>
                        </div>
                      ) : (
                        <button
                          className="edit-btn"
                          onClick={() => handleEditTask(task._id)}
                          disabled={
                            isSubmitting ||
                            ["Completed", "Cancelled"].includes(task.status)
                          }
                          style={{
                            opacity: ["Completed", "Cancelled"].includes(
                              task.status,
                            )
                              ? 0.5
                              : 1,
                            cursor: ["Completed", "Cancelled"].includes(
                              task.status,
                            )
                              ? "not-allowed"
                              : "pointer",
                          }}
                        >
                          Edit
                        </button>
                      )}
                    </td>
                    <td>
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteTask(task)}
                        disabled={
                          isSubmitting ||
                          editingId === task._id ||
                          (userProfile !== "admin"
                            ? true
                            : ["Completed", "Cancelled"].includes(task.status))
                        }
                        style={{
                          opacity:
                            userProfile !== "admin" ||
                            ["Completed", "Cancelled"].includes(task.status)
                              ? 0.5
                              : 1,
                          cursor:
                            userProfile !== "admin" ||
                            ["Completed", "Cancelled"].includes(task.status)
                              ? "not-allowed"
                              : "pointer",
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          !isLoading && (
            <div className="no-data-message">No tasks data found.</div>
          )
        )}
      </div>
      {deleteModal.isOpen && (
        <div className="delete-modal-overlay">
          <div className="delete-modal">
            <div className="delete-modal-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="54"
                height="54"
              >
                <path fill="none" d="M0 0h24v24H0z" />
                <path
                  fill="#ff4757"
                  d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-11.414L9.172 7.757 7.757 9.172 10.586 12l-2.829 2.828 1.415 1.415L12 13.414l2.828 2.829 1.415-1.415L13.414 12l2.829-2.828-1.415-1.415L12 10.586z"
                />
              </svg>
            </div>
            <h3>Confirm Delete</h3>
            <p>
              Are you sure you want to delete the task:
              <span className="trip-id-highlight">
                {" "}
                {deleteModal?.record?.taskName} ?
              </span>
            </p>
            <p className="warning-text">This action cannot be undone.</p>
            <div className="delete-modal-buttons">
              <button
                className="cancel-btn"
                onClick={() =>
                  setDeleteModal({ isOpen: false, recordId: null })
                }
              >
                Cancel
              </button>
              <button className="confirm-delete-btn" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Task;
